import React, { useContext } from 'react';
import { isReactRenderable, toArray } from '@rc-component/util';
import { clsx } from 'clsx';
import { isString } from '../_util/is';
import { cloneElement } from '../_util/reactNode';
import { ConfigContext } from '../config-provider';
import { Col } from '../grid';
import { ListContext } from './context';
export const Meta = React.forwardRef((props, ref) => {
    const { prefixCls: customizePrefixCls, className, avatar, title, description, ...others } = props;
    const { getPrefixCls } = useContext(ConfigContext);
    const prefixCls = getPrefixCls('list', customizePrefixCls);
    const classString = clsx(`${prefixCls}-item-meta`, className);
    const nativeElementRef = React.useRef(null);
    const hasAvatar = isReactRenderable(avatar);
    const hasTitle = isReactRenderable(title);
    const hasDescription = isReactRenderable(description);
    React.useImperativeHandle(ref, () => ({
        nativeElement: nativeElementRef.current,
    }));
    const content = (React.createElement("div", { className: `${prefixCls}-item-meta-content` },
        hasTitle && React.createElement("h4", { className: `${prefixCls}-item-meta-title` }, title),
        hasDescription && React.createElement("div", { className: `${prefixCls}-item-meta-description` }, description)));
    return (React.createElement("div", { ref: nativeElementRef, ...others, className: classString },
        hasAvatar && React.createElement("div", { className: `${prefixCls}-item-meta-avatar` }, avatar),
        (hasTitle || hasDescription) && content));
});
const InternalItem = React.forwardRef((props, ref) => {
    const { prefixCls: customizePrefixCls, children, actions, extra, styles, className, classNames: customizeClassNames, colStyle, ...others } = props;
    const { grid, itemLayout } = useContext(ListContext);
    const { getPrefixCls, list } = useContext(ConfigContext);
    const hasExtra = isReactRenderable(extra);
    const moduleClass = (moduleName) => clsx(list?.item?.classNames?.[moduleName], customizeClassNames?.[moduleName]);
    const moduleStyle = (moduleName) => ({
        ...list?.item?.styles?.[moduleName],
        ...styles?.[moduleName],
    });
    const isItemContainsTextNodeAndNotSingular = () => {
        const childNodes = toArray(children);
        const hasTextNode = childNodes.some(isString);
        return hasTextNode && childNodes.length > 1;
    };
    const isFlexMode = () => {
        if (itemLayout === 'vertical') {
            return hasExtra;
        }
        return !isItemContainsTextNodeAndNotSingular();
    };
    const prefixCls = getPrefixCls('list', customizePrefixCls);
    const actionsContent = actions && actions.length > 0 && (React.createElement("ul", { className: clsx(`${prefixCls}-item-action`, moduleClass('actions')), key: "actions", style: moduleStyle('actions') }, actions.map((action, i) => (React.createElement("li", { key: `${prefixCls}-item-action-${i}` },
        action,
        i !== actions.length - 1 && React.createElement("em", { className: `${prefixCls}-item-action-split` }))))));
    const Element = grid ? 'div' : 'li';
    const itemChildren = (React.createElement(Element, { ...others, ...(!grid ? { ref } : {}), className: clsx(`${prefixCls}-item`, {
            [`${prefixCls}-item-no-flex`]: !isFlexMode(),
        }, className) }, itemLayout === 'vertical' && hasExtra
        ? [
            React.createElement("div", { className: `${prefixCls}-item-main`, key: "content" },
                children,
                actionsContent),
            React.createElement("div", { className: clsx(`${prefixCls}-item-extra`, moduleClass('extra')), key: "extra", style: moduleStyle('extra') }, extra),
        ]
        : [children, actionsContent, cloneElement(extra, { key: 'extra' })]));
    return grid ? (React.createElement(Col, { ref: ref, flex: 1, style: colStyle }, itemChildren)) : (itemChildren);
});
const Item = InternalItem;
Item.Meta = Meta;
export default Item;
